import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { generateTimetable, TimetableEntry } from '../../utils/fixedRoutes/timetableUtils';
import { formatTimeTo12Hour } from '../../utils/fixedRoutes/timeFormatUtils';
import { generateRouteColor } from '../../utils/fixedRoutes/routeToLineConverter';
import { DEFAULT_OPERATING_HOURS } from '../../config/constants';
import { calculateHeadway, calculateFrequency } from '../../utils/fixedRoutes/headwayFrequencyCalculator';
import { calculateRouteLoopDuration } from '../../utils/fixedRoutes/vehicleCalculator';
import { buildTimetableMatrix } from '../../utils/fixedRoutes/buildTimetableMatrix';
import { fixedRouteTimetableToJourneys } from '../../utils/fixedRoutes/timetableGridAdapters';
import TimetableGridView from './TimetableGridView';
import { StopPosterDialog } from './poster/StopPosterDialog';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import type { FixedRoute } from '../../types/fixedRoutes';

import { Icon } from '@liftango/ui-icons';
interface TimetableTimelineProps {
  selectedRoute?: FixedRoute | null;
}

export function TimetableTimeline({ selectedRoute: propSelectedRoute }: TimetableTimelineProps) {
  const { t } = useTranslation('fixedRoutes');
  const { routes, stops, trafficMultiplier } = useFixedRoutes();
  const [currentTime] = useState(() => new Date());
  const [internalSelectedRoute, setInternalSelectedRoute] = useState<FixedRoute | null>(null);
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
  const [routeSearchQuery, setRouteSearchQuery] = useState('');
  const [routeSortBy, setRouteSortBy] = useState<'name-asc' | 'name-desc' | 'start-time-asc' | 'start-time-desc' | 'type'>('name-asc');
  const [timeRangeFilter, setTimeRangeFilter] = useState<'all' | 'morning' | 'afternoon' | 'evening'>('all');
  // bustimes-style grid (stops × journeys) vs. the original per-journey timeline.
  const [viewMode, setViewMode] = useState<'grid' | 'timeline'>('grid');
  // Printable stop-level timetable poster (feature-gated).
  const [posterOpen, setPosterOpen] = useState(false);

  // Prefer local selection so users can switch routes from within the timetable list.
  // External selection still syncs through the effect below.
  const selectedRoute = internalSelectedRoute ?? propSelectedRoute;

  // Sync externally selected route (e.g. map click) into local selection state.
  useEffect(() => {
    if (propSelectedRoute) {
      setInternalSelectedRoute(propSelectedRoute);
    }
  }, [propSelectedRoute?.id]);

  const stopNames = useMemo(() => {
    const map = new Map<string, string>();
    stops.forEach((stop) => {
      map.set(stop.id, stop.name);
    });
    return map;
  }, [stops]);

  // Helper function to convert time to minutes
  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const timeToSelectedServiceMinutes = React.useCallback((timeStr: string): number => {
    const routeStartTime = selectedRoute?.startTime;
    const routeEndTime = selectedRoute?.endTime;
    if (!routeStartTime) return timeToMinutes(timeStr);
    const startMinutes = timeToMinutes(routeStartTime);
    const rawEndMinutes = routeEndTime ? timeToMinutes(routeEndTime) : DEFAULT_OPERATING_HOURS.endHour * 60;
    const endMinutes = rawEndMinutes <= startMinutes ? rawEndMinutes + 24 * 60 : rawEndMinutes;
    let minutes = timeToMinutes(timeStr);
    if (endMinutes > 24 * 60 && minutes < startMinutes) {
      minutes += 24 * 60;
    }
    return minutes;
  }, [selectedRoute?.startTime, selectedRoute?.endTime]);

  const routesToDisplay = useMemo(() => {
    let filtered = routes;

    // Apply search filter
    if (routeSearchQuery.trim()) {
      const query = routeSearchQuery.toLowerCase();
      filtered = filtered.filter((route) => {
        const routeName = route.name.toLowerCase();
        const startTime = route.startTime.toLowerCase();
        const endTime = (route.endTime || '').toLowerCase();
        const isCirculatorText = route.isCirculator ? 'circulator' : '';
        
        // Search stop names in this route
        const stopNamesMatch = route.stops.some((stopConfig) => {
          const stop = stops.find((s) => s.id === stopConfig.stopId);
          return stop && stop.name.toLowerCase().includes(query);
        });
        
        return (
          routeName.includes(query) ||
          startTime.includes(query) ||
          endTime.includes(query) ||
          isCirculatorText.includes(query) ||
          stopNamesMatch
        );
      });
    }

    // Apply time range filter
    if (timeRangeFilter !== 'all') {
      filtered = filtered.filter((route) => {
        const startMinutes = timeToMinutes(route.startTime);
        switch (timeRangeFilter) {
          case 'morning':
            return startMinutes >= 5 * 60 && startMinutes < 12 * 60; // 5:00 - 11:59
          case 'afternoon':
            return startMinutes >= 12 * 60 && startMinutes < 17 * 60; // 12:00 - 16:59
          case 'evening':
            return startMinutes >= 17 * 60 || startMinutes < 5 * 60; // 17:00 - 4:59
          default:
            return true;
        }
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (routeSortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'start-time-asc': {
          const aTime = timeToMinutes(a.startTime);
          const bTime = timeToMinutes(b.startTime);
          return aTime - bTime;
        }
        case 'start-time-desc': {
          const aTime = timeToMinutes(a.startTime);
          const bTime = timeToMinutes(b.startTime);
          return bTime - aTime;
        }
        case 'type': {
          // Circulators first, then regular routes
          const aIsCirc = a.isCirculator ? 0 : 1;
          const bIsCirc = b.isCirculator ? 0 : 1;
          if (aIsCirc !== bIsCirc) return aIsCirc - bIsCirc;
          return a.name.localeCompare(b.name);
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [routes, routeSearchQuery, routeSortBy, timeRangeFilter, stops]);

  // Auto-select first route if none selected and routes are available
  useEffect(() => {
    if (!selectedRoute && routesToDisplay.length > 0 && !propSelectedRoute) {
      setInternalSelectedRoute(routesToDisplay[0]);
    }
  }, [routesToDisplay, selectedRoute, propSelectedRoute]);

  // Performance optimization: Cache timetables to avoid regenerating on every render
  const timetableCacheRef = useRef<Map<string, ReturnType<typeof generateTimetable>>>(new Map());

  // Helper function to create a hash of route segments for cache invalidation
  const createSegmentsHash = (route: FixedRoute): string => {
    if (!route.segments || route.segments.length === 0) {
      return 'no-segments';
    }
    // Create a hash from segment travel times and distances
    // This ensures cache invalidates when segments are updated (e.g., after manual route editing)
    return route.segments
      .map(seg => `${seg.fromStopId}-${seg.toStopId}:${seg.travelTimeMinutes || 0}:${seg.distanceMeters || 0}`)
      .join('|');
  };

  const selectedRouteTimetable = useMemo(() => {
    if (!selectedRoute) return null;

    const cache = timetableCacheRef.current;
    const routeTrafficMultiplier = selectedRoute.trafficMultiplier ?? trafficMultiplier;
    const vehicleCount = selectedRoute.isCirculator && selectedRoute.vehicleCount && selectedRoute.vehicleCount > 1
      ? selectedRoute.vehicleCount
      : 1;
    const segmentsHash = createSegmentsHash(selectedRoute);
    const cacheKey = `${selectedRoute.id}-${routeTrafficMultiplier}-${selectedRoute.startTime}-${selectedRoute.endTime || ''}-${vehicleCount}-${segmentsHash}`;
    
    // Check cache first
    let timetable = cache.get(cacheKey);
    
    if (!timetable) {
      // Generate timetable and cache it
      timetable = generateTimetable(
        selectedRoute,
        routeTrafficMultiplier,
        stopNames,
        DEFAULT_OPERATING_HOURS.startHour,
        DEFAULT_OPERATING_HOURS.endHour
      );
      cache.set(cacheKey, timetable);
    }
    
    return { route: selectedRoute, timetable };
  }, [selectedRoute, trafficMultiplier, stopNames]);

  const toggleRoute = (routeId: string) => {
    setExpandedRoutes((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) {
        next.delete(routeId);
      } else {
        next.add(routeId);
      }
      return next;
    });
  };

  const handleRouteSelect = (route: FixedRoute) => {
    setInternalSelectedRoute(route);
  };

  // Determine which stop is "next" based on current time
  const getNextStopIndex = (entries: TimetableEntry[]): number => {
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const adjustedCurrentMinutes = selectedRoute ? timeToSelectedServiceMinutes(
      `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`
    ) : currentMinutes;
    
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const arrivalMinutes = timeToSelectedServiceMinutes(entry.arrivalTime);
      
      if (arrivalMinutes > adjustedCurrentMinutes) {
        return i;
      }
    }
    return -1; // All stops have passed
  };

  // Check if we have routes but they're all filtered out
  const hasRoutesButFiltered = routes.length > 0 && routesToDisplay.length === 0;
  const hasNoRoutes = routes.length === 0;

  // Get sorted entries for selected route
  const sortedEntries = useMemo(() => {
    if (!selectedRouteTimetable) return [];
    return [...selectedRouteTimetable.timetable.timetableEntries].sort((a, b) => {
      return timeToSelectedServiceMinutes(a.arrivalTime) - timeToSelectedServiceMinutes(b.arrivalTime);
    });
  }, [selectedRouteTimetable, timeToSelectedServiceMinutes]);

  const INITIAL_DISPLAY_LIMIT = 10;
  const isExpanded = selectedRoute ? expandedRoutes.has(selectedRoute.id) : false;
  const displayLimit = isExpanded ? sortedEntries.length : INITIAL_DISPLAY_LIMIT;
  const entriesToDisplay = sortedEntries.slice(0, displayLimit);
  const hasMore = sortedEntries.length > INITIAL_DISPLAY_LIMIT;
  const nextStopIndex = getNextStopIndex(sortedEntries);
  const routeColor = selectedRoute ? (selectedRoute.color || generateRouteColor(selectedRoute.id)) : undefined;
  
  // Generate vehicle colors for multi-vehicle circulator routes
  const getVehicleColor = (vehicleIndex: number | undefined, totalVehicles: number): string | undefined => {
    if (vehicleIndex === undefined || totalVehicles <= 1 || !selectedRoute) {
      return routeColor;
    }
    
    // Generate distinct colors for each vehicle
    // Use HSL with evenly spaced hues
    const hueStep = 360 / totalVehicles;
    const hue = (vehicleIndex * hueStep) % 360;
    // Use slightly different saturation/lightness to distinguish from route color
    return `hsl(${hue}, 75%, 45%)`;
  };
  
  const vehicleCount = selectedRoute?.isCirculator && selectedRoute?.vehicleCount && selectedRoute.vehicleCount > 1
    ? selectedRoute.vehicleCount
    : 1;

  // Group entries by vehicle for multi-vehicle routes
  const entriesByVehicle = useMemo(() => {
    if (vehicleCount <= 1) {
      return null; // Single vehicle, no grouping needed
    }
    
    const grouped = new Map<number, TimetableEntry[]>();
    for (let i = 0; i < vehicleCount; i++) {
      grouped.set(i, []);
    }
    
    sortedEntries.forEach(entry => {
      if (entry.vehicleIndex !== undefined) {
        const vehicleEntries = grouped.get(entry.vehicleIndex) || [];
        vehicleEntries.push(entry);
        grouped.set(entry.vehicleIndex, vehicleEntries);
      }
    });
    
    // Sort each vehicle's entries by arrival time
    grouped.forEach((entries, vehicleIndex) => {
      entries.sort((a, b) => {
        const [h1, m1] = a.arrivalTime.split(':').map(Number);
        const [h2, m2] = b.arrivalTime.split(':').map(Number);
        const time1 = h1 * 60 + m1;
        const time2 = h2 * 60 + m2;
        return time1 - time2;
      });
    });
    
    return grouped;
  }, [sortedEntries, vehicleCount]);

  // Calculate summary info for multi-vehicle routes
  const summaryInfo = useMemo(() => {
    if (!selectedRoute || vehicleCount <= 1) return null;
    
    const routeTrafficMultiplier = selectedRoute.trafficMultiplier ?? trafficMultiplier;
    const loopDuration = calculateRouteLoopDuration(selectedRoute, routeTrafficMultiplier);
    const headway = calculateHeadway(loopDuration, vehicleCount);
    const frequency = headway !== null ? calculateFrequency(headway) : null;
    
    return {
      loopDuration: Math.round(loopDuration),
      headway: headway !== null ? Math.round(headway * 10) / 10 : null,
      frequency: frequency !== null ? frequency.toFixed(1) : null,
    };
  }, [selectedRoute, vehicleCount, trafficMultiplier]);

  // Stop metadata (name + coordinates) for the grid + companion map, keyed by stop id.
  const ownRouteStopMeta = useMemo(() => {
    const map = new Map<string, { name?: string | null; lat?: number; lng?: number }>();
    stops.forEach((s) => map.set(s.id, { name: s.name, lat: s.coordinates?.lat, lng: s.coordinates?.lng }));
    return map;
  }, [stops]);

  // bustimes-style matrix for the selected own-route timetable.
  const gridMatrix = useMemo(() => {
    if (!selectedRouteTimetable || !selectedRoute) return null;
    const journeys = fixedRouteTimetableToJourneys(selectedRouteTimetable.timetable, selectedRoute);
    return buildTimetableMatrix(journeys, (id) => ownRouteStopMeta.get(id));
  }, [selectedRouteTimetable, selectedRoute, ownRouteStopMeta]);

  // Companion map: real route geometry where available, plus the route's stops (deduped).
  const gridMiniMap = useMemo(() => {
    if (!selectedRoute) return undefined;
    const coordinates = (selectedRoute.geometry?.coordinates ?? []) as Array<[number, number]>;
    const seen = new Set<string>();
    const mmStops = selectedRoute.stops
      .map((sc) => {
        const s = stops.find((x) => x.id === sc.stopId);
        return s && s.coordinates
          ? { stopId: s.id, name: s.name, lat: s.coordinates.lat, lng: s.coordinates.lng }
          : null;
      })
      .filter((x): x is { stopId: string; name: string; lat: number; lng: number } => x !== null)
      .filter((s) => (seen.has(s.stopId) ? false : (seen.add(s.stopId), true)));
    return { coordinates, stops: mmStops };
  }, [selectedRoute, stops]);

  return (
    <Card elevation={0} sx={{ boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}>
      <CardHeader
        sx={{ px: 0 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="bus" />
            <Typography variant="h6">{t('timetableTimeline.title')}</Typography>
          </Box>
        }
        subheader={t('timetableTimeline.subheader')}
      />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {hasNoRoutes ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Icon size={48} name="bus" style={{ color: '#52565e', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('timetableTimeline.noRoutesTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('timetableTimeline.noRoutesDescription')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '350px 1fr', height: '600px', borderTop: 1, borderColor: 'divider' }}>
            {/* Left Panel: Route List */}
            <Box sx={{ borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Search and Filter Controls */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <TextField
                  placeholder={t('timetableTimeline.searchPlaceholder')}
                  value={routeSearchQuery}
                  onChange={(e) => setRouteSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon size={20} name="magnifying-glass" style={{ color: '#52565e' }} />
                      </InputAdornment>
                    ),
                    endAdornment: routeSearchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setRouteSearchQuery('')}
                          edge="end"
                        >
                          <Icon name="xmark" size={20} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel>{t('timetableTimeline.sortBy')}</InputLabel>
                    <Select
                      value={routeSortBy}
                      onChange={(e) => setRouteSortBy(e.target.value as typeof routeSortBy)}
                      label={t('timetableTimeline.sortBy')}
                    >
                      <MenuItem value="name-asc">{t('timetableTimeline.sortOptions.nameAsc')}</MenuItem>
                      <MenuItem value="name-desc">{t('timetableTimeline.sortOptions.nameDesc')}</MenuItem>
                      <MenuItem value="start-time-asc">{t('timetableTimeline.sortOptions.startTimeAsc')}</MenuItem>
                      <MenuItem value="start-time-desc">{t('timetableTimeline.sortOptions.startTimeDesc')}</MenuItem>
                      <MenuItem value="type">{t('timetableTimeline.sortOptions.type')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel>{t('timetableTimeline.timeRange')}</InputLabel>
                    <Select
                      value={timeRangeFilter}
                      onChange={(e) => setTimeRangeFilter(e.target.value as typeof timeRangeFilter)}
                      label={t('timetableTimeline.timeRange')}
                    >
                      <MenuItem value="all">{t('timetableTimeline.timeRangeOptions.all')}</MenuItem>
                      <MenuItem value="morning">{t('timetableTimeline.timeRangeOptions.morning')}</MenuItem>
                      <MenuItem value="afternoon">{t('timetableTimeline.timeRangeOptions.afternoon')}</MenuItem>
                      <MenuItem value="evening">{t('timetableTimeline.timeRangeOptions.evening')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {hasRoutesButFiltered && (
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setRouteSearchQuery('');
                      setTimeRangeFilter('all');
                    }}
                    sx={{ mt: 2 }}
                  >
                    {t('timetableTimeline.clearFilters')}
                  </Button>
                )}
              </Box>

              {/* Route List */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {hasRoutesButFiltered ? (
                  <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('timetableTimeline.noMatch')}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {routesToDisplay.map((route, index) => {
                      const routeColor = route.color || generateRouteColor(route.id);
                      const isSelected = selectedRoute?.id === route.id;
                      
                      return (
                        <React.Fragment key={route.id}>
                          {index > 0 && <Divider />}
                          <ListItem disablePadding>
                            <ListItemButton
                              selected={isSelected}
                              onClick={() => handleRouteSelect(route)}
                              sx={{
                                py: 1.5,
                                px: 2,
                                '&.Mui-selected': {
                                  backgroundColor: 'action.selected',
                                  '&:hover': {
                                    backgroundColor: 'action.selected',
                                  },
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: routeColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon name="bus" size={20} />
                                </Box>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={isSelected ? 600 : 500} noWrap>
                                        {route.name}
                                      </Typography>
                                      {route.isCirculator && (
                                        <Chip
                                          label={t('timetableTimeline.circulator')}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {formatTimeTo12Hour(route.startTime)}
                                      {route.endTime && ` - ${formatTimeTo12Hour(route.endTime)}`}
                                    </Typography>
                                  }
                                />
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Box>

            {/* Right Panel: Selected Route Details */}
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!selectedRoute ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Icon size={64} name="bus" style={{ color: '#52565e', marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {t('timetableTimeline.selectRouteTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('timetableTimeline.selectRouteDescription')}
                    </Typography>
                  </Box>
                </Box>
              ) : !selectedRouteTimetable ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('timetableTimeline.loadingTimetable')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  {/* Route Header */}
                  <Paper elevation={0} sx={{ p: 2, borderBottom: 1, borderColor: 'divider', borderRadius: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          backgroundColor: routeColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={20} name="bus" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" fontWeight="600" noWrap>
                            {selectedRouteTimetable.timetable.routeName}
                          </Typography>
                          {selectedRoute.isCirculator && (
                            <Chip
                              label={t('timetableTimeline.circulator')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 22 }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('timetableTimeline.startsAt', { time: formatTimeTo12Hour(selectedRoute.startTime) })}
                          {selectedRoute.endTime && ` • ${t('timetableTimeline.endsAt', { time: formatTimeTo12Hour(selectedRoute.endTime) })}`}
                          {' • '}
                          {t('timetableTimeline.stopsCount', { count: selectedRoute.stops.length })}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Icon name="file-arrow-down" size={18} />}
                        onClick={() => setPosterOpen(true)}
                        sx={{ flexShrink: 0, mr: 1 }}
                      >
                        {t('timetableTimeline.printStopView', 'Print stop-level view')}
                      </Button>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={viewMode}
                        onChange={(_e, next) => next && setViewMode(next)}
                        aria-label={t('timetableTimeline.viewMode')}
                        sx={{ flexShrink: 0 }}
                      >
                        <ToggleButton value="grid" aria-label={t('timetableTimeline.gridView')}>
                          <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('timetableTimeline.gridView')}
                        </ToggleButton>
                        <ToggleButton value="timeline" aria-label={t('timetableTimeline.timelineView')}>
                          <ViewTimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('timetableTimeline.timelineView')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Paper>

                  {/* Summary Info for Multi-Vehicle Routes */}
                  {summaryInfo && vehicleCount > 1 && (
                    <Paper elevation={0} sx={{ p: 2, borderBottom: 1, borderColor: 'divider', borderRadius: 0, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {t('timetableTimeline.vehicles')}
                          </Typography>
                          <Typography variant="h6" fontWeight="600">
                            {vehicleCount}
                          </Typography>
                        </Box>
                        {summaryInfo.loopDuration !== null && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {t('timetableTimeline.loopDuration')}
                            </Typography>
                            <Typography variant="h6" fontWeight="600">
                              {t('timetableTimeline.minutesShort', { value: summaryInfo.loopDuration })}
                            </Typography>
                          </Box>
                        )}
                        {summaryInfo.headway !== null && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {t('timetableTimeline.headway')}
                            </Typography>
                            <Typography variant="h6" fontWeight="600">
                              {t('timetableTimeline.minutesShort', { value: summaryInfo.headway })}
                            </Typography>
                          </Box>
                        )}
                        {summaryInfo.frequency !== null && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {t('timetableTimeline.frequency')}
                            </Typography>
                            <Typography variant="h6" fontWeight="600">
                              {t('timetableTimeline.vehiclesPerHour', { value: summaryInfo.frequency })}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {/* Timetable: bustimes-style grid (default) or the per-journey timeline */}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: vehicleCount > 1 ? 2 : 3 }}>
                    {viewMode === 'grid' && gridMatrix ? (
                      <TimetableGridView
                        matrix={gridMatrix}
                        routeColor={routeColor}
                        miniMap={gridMiniMap}
                        onModeChange={(mode) =>
                          trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.timetable_grid_mode_toggled, {
                            surface: 'fixed_route',
                            mode,
                            route_id: selectedRoute.id,
                            timestamp: new Date().toISOString(),
                          })
                        }
                      />
                    ) : sortedEntries.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('timetableTimeline.noTimetableEntries')}
                        </Typography>
                      </Box>
                    ) : vehicleCount > 1 && entriesByVehicle ? (
                      // Multi-vehicle: Show columns
                      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${vehicleCount}, 1fr)`, gap: 2 }}>
                        {Array.from(entriesByVehicle.entries()).map(([vehicleIndex, vehicleEntries]) => {
                          const vehicleColor = getVehicleColor(vehicleIndex, vehicleCount);
                          const vehicleNextStopIndex = getNextStopIndex(vehicleEntries);
                          
                          return (
                            <Box key={vehicleIndex} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                              {/* Vehicle Header */}
                              <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '50%',
                                      backgroundColor: vehicleColor,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Icon size={18} name="bus" />
                                  </Box>
                                  <Typography variant="h6" fontWeight="600">
                                    {t('timetableTimeline.vehicleLabel', { number: vehicleIndex + 1 })}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('timetableTimeline.stopsCount', { count: vehicleEntries.length })}
                                </Typography>
                              </Box>

                              {/* Vehicle Timeline */}
                              <Box sx={{ position: 'relative' }}>
                                {vehicleEntries.map((entry, entryIndex) => {
                                  const isLast = entryIndex === vehicleEntries.length - 1;
                                  const sortedIndex = vehicleEntries.findIndex(
                                    (e) => e.stopId === entry.stopId &&
                                           e.arrivalTime === entry.arrivalTime &&
                                           e.loopNumber === entry.loopNumber
                                  );
                                  const isNextStop = sortedIndex === vehicleNextStopIndex;
                                  const loopIndicator = entry.loopNumber > 1 ? t('timetableTimeline.loopIndicator', { number: entry.loopNumber }) : '';

                                  return (
                                    <Box
                                      key={`${entry.stopId}-${entry.arrivalTime}-${entry.loopNumber}-${entryIndex}`}
                                      sx={{ position: 'relative' }}
                                    >
                                      {/* Vertical connector line */}
                                      {!isLast && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            left: '16px',
                                            top: '40px',
                                            width: '2px',
                                            height: 'calc(100% - 8px)',
                                            backgroundColor: vehicleColor || 'divider',
                                            opacity: 0.5,
                                            zIndex: 0,
                                          }}
                                        />
                                      )}

                                      {/* Timeline item */}
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 1.5,
                                          pb: 2,
                                          position: 'relative',
                                          zIndex: 1,
                                        }}
                                      >
                                        {/* Circular icon */}
                                        <Box
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: vehicleColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            border: '2px solid',
                                            borderColor: 'background.paper',
                                            position: 'relative',
                                            zIndex: 2,
                                            flexShrink: 0,
                                            boxShadow: isNextStop && vehicleColor ? `0 0 0 3px ${vehicleColor}40` : 'none',
                                          }}
                                        >
                                          <Icon size={18} name="location-dot" />
                                        </Box>

                                        {/* Stop details */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          {isNextStop && (
                                            <Chip
                                              label={t('timetableTimeline.next')}
                                              size="small"
                                              color="primary"
                                              sx={{ mb: 0.5, height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                            />
                                          )}
                                          <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                                            {entry.stopName}{loopIndicator}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {formatTimeTo12Hour(entry.arrivalTime)}
                                            {entry.arrivalTime !== entry.departureTime && t('timetableTimeline.departsBullet', { time: formatTimeTo12Hour(entry.departureTime) })}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      // Single vehicle: Show original timeline
                      <>
                        <Box sx={{ position: 'relative' }}>
                          {entriesToDisplay.map((entry, entryIndex) => {
                            const isLast = entryIndex === entriesToDisplay.length - 1;
                            const sortedIndex = sortedEntries.findIndex(
                              (e) => e.stopId === entry.stopId &&
                                     e.arrivalTime === entry.arrivalTime &&
                                     e.loopNumber === entry.loopNumber &&
                                     e.vehicleIndex === entry.vehicleIndex
                            );
                            const isNextStop = sortedIndex === nextStopIndex;
                            const loopIndicator = entry.loopNumber > 1 ? t('timetableTimeline.loopIndicator', { number: entry.loopNumber }) : '';
                            const vehicleIndicator = entry.vehicleIndex !== undefined && vehicleCount > 1
                              ? t('timetableTimeline.vehicleIndicator', { number: entry.vehicleIndex + 1 })
                              : '';
                            
                            // Get vehicle-specific color
                            const entryColor = getVehicleColor(entry.vehicleIndex, vehicleCount);

                            return (
                              <Box
                                key={`${entry.stopId}-${entry.arrivalTime}-${entry.loopNumber}-${entry.vehicleIndex ?? 0}-${entryIndex}`}
                                sx={{ position: 'relative' }}
                              >
                                {/* Vertical connector line */}
                                {!isLast && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: '28px',
                                      top: '56px',
                                      width: '2px',
                                      height: 'calc(100% - 8px)',
                                      backgroundColor: entryColor || 'divider',
                                      opacity: 0.5,
                                      zIndex: 0,
                                    }}
                                  />
                                )}

                                {/* Timeline item */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    pb: 3,
                                    position: 'relative',
                                    zIndex: 1,
                                  }}
                                >
                                  {/* Circular icon */}
                                  <Box
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      borderRadius: '50%',
                                      backgroundColor: entryColor,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      border: '2px solid',
                                      borderColor: 'background.paper',
                                      position: 'relative',
                                      zIndex: 2,
                                      flexShrink: 0,
                                      boxShadow: isNextStop && entryColor ? `0 0 0 4px ${entryColor}40` : 'none',
                                    }}
                                  >
                                    <Icon size={20} name="location-dot" />
                                  </Box>

                                  {/* Stop details */}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {isNextStop && (
                                      <Chip
                                        label={t('timetableTimeline.nextStop')}
                                        size="small"
                                        color="primary"
                                        sx={{ mb: 0.5, height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                      />
                                    )}
                                    <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 0.5 }}>
                                      {entry.stopName}{loopIndicator}{vehicleIndicator}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Icon name="clock" size={20} style={{ color: '#52565e' }} />
                                        <Typography variant="body2" color="text.secondary">
                                          {t('timetableTimeline.arrives', { time: formatTimeTo12Hour(entry.arrivalTime) })}
                                        </Typography>
                                      </Box>
                                      {entry.arrivalTime !== entry.departureTime && (
                                        <>
                                          <Typography variant="body2" color="text.secondary">•</Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {t('timetableTimeline.departs', { time: formatTimeTo12Hour(entry.departureTime) })}
                                          </Typography>
                                        </>
                                      )}
                                    </Box>
                                  </Box>

                                  {/* Time display */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                    <Typography variant="h6" fontWeight="600" color="primary">
                                      {formatTimeTo12Hour(entry.arrivalTime)}
                                    </Typography>
                                    {entry.arrivalTime !== entry.departureTime && (
                                      <Typography variant="caption" color="text.secondary">
                                        {t('timetableTimeline.departsShort', { time: formatTimeTo12Hour(entry.departureTime) })}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>

                        {/* Show More/Less Button */}
                        {hasMore && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                            <Button
                              variant="outlined"
                              onClick={() => toggleRoute(selectedRoute.id)}
                              sx={{
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                },
                              }}
                            >
                              {isExpanded
                                ? t('timetableTimeline.showLess')
                                : t('timetableTimeline.showMore', { count: sortedEntries.length - INITIAL_DISPLAY_LIMIT })}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>

      <StopPosterDialog
        open={posterOpen}
        onClose={() => setPosterOpen(false)}
        route={selectedRoute ?? null}
        routes={routes}
        stops={stops}
      />
    </Card>
  );
}

export default TimetableTimeline;
